import { EnumUtility } from '@codequality/vue-cli-base';
import _ from 'lodash';

import { E0円非表示フラグ, Eモニタリング損益差異分析基準値 } from '@/openapi/enum';
import { データListItemResponse, 勘定科目ListItemResponse, 帳票DynamicConfig } from '@/openapi/user';

import AppFormatUtility from '@/classes/common/AppFormatUtility';
import { MONTHS_OF_YEAR } from '@/classes/common/Constants';
import 決算期Utility, { 決算期TableHeader } from '@/classes/common/決算期Utility';
import AbstractPlbscfService, {
  PRINT_TEMPLATE_CONFIGS,
  PlbscfListItem,
  PlbscfTypeList,
  PlbscfYearValue,
  PrintTemplateConfig,
} from '@/classes/service/AbstractPlbscfService';
import AbstractデータService, { ServiceType, 帳票データ } from '@/classes/service/AbstractデータService';
import Abstract計画PlbscfService, { 帳票Type, 業績予測 } from '@/classes/service/Abstract計画PlbscfService';
import AbstractMonitoringService from '@/classes/service/モニタリング/AbstractMonitoringService';
import { PlbscfListItemConfig, PlbscfType, RowCssClass, ValueType, 損益_CALC_PROCESS_MAP } from '@/master/Plbscf';

// ------------------------------------------------------------------
// types
// ------------------------------------------------------------------
/**
 * 月次推移・累計で共通のものを定義
 */
type 差異分析ListItem = {
  config: PlbscfListItemConfig;
  勘定科目?: 勘定科目ListItemResponse;
};
/**
 * 月次推移
 */
export type 月次推移 = {
  header: 決算期TableHeader[]; // size: 列数
  list: 月次推移ListItem[]; // size: 行数
};
export type 月次推移ListItem = 差異分析ListItem & {
  value?: {
    計画: number[]; // size: 12ヶ月分
    実績: PlbscfYearValue;
    差異?: number[]; // size: 12ヶ月分
  };
};
/**
 * 累計
 */
export type 累計 = {
  header: 決算期TableHeader[]; // size: 列数
  list: 累計ListItem[]; // size: 行数
};
export type 累計ListItem = 差異分析ListItem & {
  value?: {
    計画: Record<keyof typeof Eモニタリング損益差異分析基準値, number>;
    実績: number;
    業績予測: 業績予測;
  };
};
/**
 * parse の返却値
 */
export type 差異分析 = {
  // 貸借
  bs: {
    月次推移: 月次推移;
  };
  // 損益
  pls: {
    月次推移: 月次推移;
    累計: 累計;
    部門pk?: number;
  }[]; // size：部門数 + 1（合計）
};

// ------------------------------------------------------------------
// const
// ------------------------------------------------------------------
/**
 * 帳票テンプレート行の設定
 */
const 月次推移TemplateConfig: PrintTemplateConfig[] = [
  {
    type: PlbscfType.貸借対照表,
    list: {
      header: 0,
      row: {
        body: 1,
        specialHeader: 4,
        header: 7,
        subHeader: 10,
        itemFirst: 13,
        itemSecond: 16,
        diff: 19,
      },
    },
  },
  {
    type: PlbscfType.損益計算書,
    list: {
      header: 11,
      row: {
        body: 12,
        title: 15,
        specialHeader: 16,
        header: 21,
        subHeader: 24,
        itemFirst: 27,
        itemSecond: 30,
        grey: 33,
      },
      unitNotYenRow: {
        specialHeader: 19,
      },
    },
  },
];

const 売上総利益率Code = 'N215';

// ------------------------------------------------------------------
// 差異分析Service
// ------------------------------------------------------------------
export default class 差異分析Service extends AbstractMonitoringService<差異分析> {
  // ------------------------------------------------------------------
  // fields
  // ------------------------------------------------------------------
  private static 差異分析: 差異分析 | null = null;

  protected serviceType = ServiceType.差異分析;

  private 累計差異比率Header: 決算期TableHeader[] = [];

  // ------------------------------------------------------------------
  // public static methods
  // ------------------------------------------------------------------

  /**
   * 初期化
   *
   * ※帳票一括印刷 のボタン押下時、選択した帳票でループする前に本処理を実行する（別タブでデータが更新されている可能性があるため）
   * ※帳票一括印刷 の最中（ループ中）は、再計算させないために「this.差異分析」を使用して帳票出力する
   */
  public static initialize(): void {
    this.差異分析 = null;
  }

  /**
   * 基準値に応じて、listを加工して返却
   *
   * @param list
   * @param e基準値
   * @returns
   */
  public static get累計ListItemsBy基準値(
    list: 累計ListItem[],
    e基準値: Eモニタリング損益差異分析基準値
  ): 累計ListItem[] {
    const key = this.get基準値Key(e基準値);
    return list.map((l) => {
      if (l.value === undefined) return l;
      return {
        ...l,
        value: {
          ...l.value,
          業績予測: l.config.unitNotYen === undefined ? this.calculate業績予測(l.value.実績, l.value.計画[key]) : {},
        },
      };
    });
  }

  /**
   * 基準値のenum keyを取得
   *
   * @param e基準値
   * @returns
   */
  public static get基準値Key(e基準値: Eモニタリング損益差異分析基準値): keyof typeof Eモニタリング損益差異分析基準値 {
    return EnumUtility.valueToKey(
      Eモニタリング損益差異分析基準値,
      e基準値
    ) as keyof typeof Eモニタリング損益差異分析基準値;
  }

  // ------------------------------------------------------------------
  // override
  // ------------------------------------------------------------------

  /**
   * @override
   * @param データs
   * @param option
   * @returns
   */
  public parse(
    データs: データListItemResponse[],
    option?: Record<string, string | number | AbstractデータService<unknown>>
  ): 差異分析 {
    // 帳票かつ既に計算済の場合は再計算しない
    if (this.is帳票 && 差異分析Service.差異分析 !== null) return 差異分析Service.差異分析;

    // 初期化
    this.initializeService(データs, option);

    // 画面表示用データを生成
    const v差異分析 = this.calcAndCreate差異分析();

    // 帳票で再計算させないために詰めておく
    差異分析Service.差異分析 = v差異分析;
    return v差異分析;
  }

  /**
   * @override
   * @param データs
   * @param option
   * @returns
   */
  public parse帳票ListItemRequest(
    データs: データListItemResponse[],
    option?: Record<string, string | number | AbstractデータService<unknown>>
  ): 帳票データ[] {
    const v差異分析 = this.parse(データs, option);

    if (option === undefined) throw new Error('no option');
    const plbscfType = option.plbscfType as PlbscfType;

    let list: 帳票データ[] = [];
    if (plbscfType === PlbscfType.貸借対照表) {
      // 貸借
      list = [this.create月次推移帳票データ(v差異分析.bs.月次推移, plbscfType)];
    } else {
      // 損益
      const e基準値 = option.referenceValue as Eモニタリング損益差異分析基準値;
      const v帳票Type = option.帳票Type as 帳票Type;

      if (v帳票Type === 帳票Type.標準) {
        // 部門合計
        const pl = v差異分析.pls.find((pl) => pl.部門pk === undefined)!;
        list = this.createPl帳票データs(pl, e基準値);
      } else {
        // 部門別（シート数 = 部門数）
        v差異分析.pls
          .filter((pl) => pl.部門pk !== undefined)
          .forEach((pl) => list.push(...this.createPl帳票データs(pl, e基準値)));
      }
    }

    this.setIndexedXlsxSheetNames(list);
    return list;
  }

  // ------------------------------------------------------------------
  // public methods
  // ------------------------------------------------------------------

  /**
   * 再計算し、画面表示用データを生成
   *
   * @returns
   */
  public calcAndCreate差異分析(): 差異分析 {
    this.initializeProperty();

    // plbscfの計算用データに計算結果を格納
    this.calcPlbscfValues();

    return this.create差異分析();
  }

  /**
   * 画面表示用データを生成
   *
   * @returns
   */
  public create差異分析(): 差異分析 {
    const 計画書 = AbstractデータService.計画書;
    const is0円非表示 = 計画書.cモニタリング0円非表示フラグ === E0円非表示フラグ.非表示;
    const 会計期間 = 計画書.cモニタリング会計期間 ?? 計画書.c計画0期の決算期;
    const 会計期間Year = 決算期Utility.getYear(会計期間);
    const 月次推移Header = this.all決算期.header.filter((h) => h.決算年 === 会計期間Year && h.month !== undefined);
    const 累計Header: 決算期TableHeader[] = [
      {
        label: `実績\n${AppFormatUtility.format年月期(会計期間)}`,
        year: 会計期間Year,
        決算年: 会計期間Year,
        isSummary: true,
      },
      ...this.累計差異比率Header,
    ];

    // 計画書データの状態に応じて返却値を絞り込む
    // 行データ絞り込み
    const allTypes: PlbscfTypeList[] = this.calcTypeLists.map((t) => {
      let list = t.list.map((listItem) => ({
        ...listItem,
        yearValues: listItem.yearValues.filter((yv) => yv.year === 会計期間Year),
      }));
      // 0円非表示フラグ
      if (is0円非表示) list = this.filterNotZero(list);
      if (t.type === PlbscfType.損益計算書) list = this.filter製造原価科目未使用(list);
      return {
        ...t,
        list,
      };
    });

    const 貸借typeList = allTypes.find((t) => t.type === PlbscfType.貸借対照表)!;
    const 損益typeLists = allTypes.filter((t) => t.type === PlbscfType.損益計算書);

    return {
      bs: {
        月次推移: {
          header: 月次推移Header,
          list: this.create月次推移ListItems(貸借typeList.list),
        },
      },
      pls: 損益typeLists.map((t) => ({
        月次推移: {
          header: 月次推移Header,
          list: this.create月次推移ListItems(t.list),
        },
        累計: {
          header: 累計Header,
          list: this.create累計ListItems(t.list),
        },
        部門pk: t.部門pk,
      })),
    };
  }

  // ------------------------------------------------------------------
  // override protected methods
  // ------------------------------------------------------------------

  // @override
  protected transcribe(): void {
    // 差異分析は転記不要
  }

  // @override
  protected copyBy計画PlbscfService(
    データs: データListItemResponse[],
    option?: Record<string, string | number | AbstractデータService<unknown>>
  ): void {
    super.copyBy計画PlbscfService(データs, option);

    // 分析データは必要な分だけに絞っておく
    this.分析PlbscfTypeLists = this.分析PlbscfTypeLists.filter((t) =>
      [PlbscfType.貸借対照表, PlbscfType.損益計算書].includes(t.type)
    );
  }

  // @override
  protected create決算期Headers(): void {
    // 決算期
    this.all決算期 = 決算期Utility.getモニタリング決算期(AbstractデータService.事業所.c計画作成期間);
    this.onlyYear決算期 = {
      header: this.all決算期.header.filter((h) => h.month === undefined),
      years: this.all決算期.years,
    };
    // ヘッダー
    this.累計差異比率Header = 決算期Utility.create差異比率Header('実績', '計画');
  }

  // ------------------------------------------------------------------
  // private methods
  // ------------------------------------------------------------------

  /**
   * 月次推移一覧を作成する
   *
   * @param list
   * @returns
   */
  private create月次推移ListItems(list: PlbscfListItem[]): 月次推移ListItem[] {
    return list.map((l) => {
      const listItem: 月次推移ListItem = { config: l.config, 勘定科目: l.勘定科目 };
      if (l.config.valueType === ValueType.noValue) return listItem;

      listItem.value = {
        計画: Abstract計画PlbscfService.getMonthlyValues(l.monitoring!.yearValue),
        実績: l.yearValues[0],
      };
      // 差異を計算
      if (l.config.unitNotYen === undefined && l.config.rowCssClass !== RowCssClass.diffRow) {
        const 実績Values = Abstract計画PlbscfService.getMonthlyValues(listItem.value.実績);
        listItem.value.差異 = 実績Values.map((v, i) => v - listItem.value!.計画[i]);
      }

      return listItem;
    });
  }

  /**
   * 累計一覧を作成する
   *
   * @param list
   * @returns
   */
  private create累計ListItems(list: PlbscfListItem[]): 累計ListItem[] {
    const 累計ListItems = list.map((l) => {
      const listItem: 累計ListItem = { config: l.config, 勘定科目: l.勘定科目 };
      if (l.config.valueType === ValueType.noValue) return listItem;

      const 計画yv = l.monitoring!.yearValue;
      listItem.value = {
        計画: {
          年度合計: AbstractPlbscfService.getYearValue(計画yv),
          直近実績月: this.get直近実績Value(計画yv),
        },
        実績: AbstractPlbscfService.getYearValue(l.yearValues[0]),
        業績予測: {},
      };

      return listItem;
    });

    // 直近実績月：「売上総利益率(%)」の算出
    const findListItemByCode = (code: string): 累計ListItem =>
      累計ListItems.find(
        (l) => this.isCodeFilter(l.config.valueType, l.config.filter) && l.config.filter.code === code
      )!;
    const get直近実績月ValueByCode = (code: string): number => findListItemByCode(code).value!.計画.直近実績月;
    const division = 損益_CALC_PROCESS_MAP.get(売上総利益率Code)!.config.codeUnique!.division;
    const listItem = findListItemByCode(売上総利益率Code);
    listItem.value!.計画.直近実績月 = this.divide(
      get直近実績月ValueByCode(division.numerator),
      get直近実績月ValueByCode(division.denominator),
      division
    );

    return 累計ListItems;
  }

  /**
   * 直近実績を取得
   *
   * @param yv
   * @returns
   */
  private get直近実績Value(yv: PlbscfYearValue): number {
    const 実績直近月Index = this.readonlyMonthIndex;
    // c実績直近月 < 最初の月　の場合、0を返却
    if (実績直近月Index === null) return 0;

    // c実績直近月 >= 最後の月　の場合、年次の値を返却
    if (実績直近月Index === MONTHS_OF_YEAR - 1) return AbstractPlbscfService.getYearValue(yv);

    // 上記以外の場合、最初の月 ～ c実績直近月 までの合計値を返却
    const mvs = Abstract計画PlbscfService.getMonthlyValues(yv);
    let value = 0;
    for (let i = 0; i < mvs.length; i++) {
      value += mvs[i];
      if (i >= 実績直近月Index) break;
    }
    return value;
  }

  /**
   * 損益の帳票データを整形
   *
   * @param pl
   * @param e基準値
   * @returns
   */
  private createPl帳票データs(
    pl: { 月次推移: 月次推移; 累計: 累計; 部門pk?: number },
    e基準値: Eモニタリング損益差異分析基準値
  ): 帳票データ[] {
    let sheetName = '損益計算書差異分析';
    if (pl.部門pk !== undefined) {
      const v部門 = this.部門s.find((b) => b.pk === pl.部門pk)!;
      sheetName = `損益計算書（部門別）差異分析-${v部門.c部門名}`;
    }
    return [
      {
        ...this.create累計帳票データ(pl.累計, e基準値),
        sheetName: `${sheetName}-累計`,
      },
      {
        ...this.create月次推移帳票データ(pl.月次推移, PlbscfType.損益計算書),
        sheetName: `${sheetName}-月次推移`,
      },
    ];
  }

  /**
   * 月次推移の帳票データを整形
   *
   * @param 月次推移
   * @param type
   * @returns
   */
  private create月次推移帳票データ(月次推移: 月次推移, type: PlbscfType): 帳票データ {
    const templateConfigList = 月次推移TemplateConfig.find((p) => p.type === type)!.list;

    // 初行設定込みで初期化
    const templateRows: number[] = [templateConfigList.header];
    const headerLabels: (string | null)[] = [
      `【月次推移】\n${AbstractデータService.getMoneyUnitLabel()}`,
      ...this.templateCellValues(2),
    ];
    const values: (string | number | null)[][] = [[...headerLabels, ...月次推移.header.map((h) => h.label)]];

    // 行でループ
    月次推移.list.forEach((l) => {
      const config = l.config;
      // excelテンプレート行を設定
      const templateConfig = this.getTemplateConfig(templateConfigList, config);
      templateRows.push(templateConfig);

      // 左端の項目ラベルを準備しておく
      const rowLabels = [...this.createConfigLabels(config), null];

      if (l.value === undefined) {
        // valueなしの場合、1行追加
        values.push([...rowLabels, ...this.templateCellValues(MONTHS_OF_YEAR)]);
      } else {
        // valueありの場合、計画/実績/差異? の最大3行追加
        templateRows.push(templateConfig + 1);
        const nullLabels = this.templateCellValues(rowLabels.length);
        // ・計画/実績
        values.push(
          [...rowLabels, ...l.value.計画.map((v) => this.getPrintFormatValue(config, v))],
          [...nullLabels, ..._.range(MONTHS_OF_YEAR).map((i) => this.getFormatValueByType(config, l.value!.実績, i))]
        );
        // ・差異
        if (l.value.差異 !== undefined) {
          templateRows.push(templateConfig + 2);
          values.push([...nullLabels, ...l.value.差異.map((v) => this.getMoneyUnitValue(v))]);
        }
      }
    });

    return {
      templateRows,
      values,
      dynamicConfigs: this.create帳票DynamicConfig(月次推移.header, headerLabels.length),
    };
  }

  /**
   * 累計の帳票データを整形
   *
   * @param 累計
   * @param e基準値
   * @returns
   */
  private create累計帳票データ(累計: 累計, e基準値: Eモニタリング損益差異分析基準値): 帳票データ {
    const templateConfigList = PRINT_TEMPLATE_CONFIGS.find((p) => p.type === PlbscfType.損益計算書)!.list;
    const 計画書 = AbstractデータService.計画書;
    const 会計期間Label = AppFormatUtility.format年月期(計画書.cモニタリング会計期間 ?? 計画書.c計画0期の決算期);
    const 基準値Key = 差異分析Service.get基準値Key(e基準値);
    const 累計ListItems = 差異分析Service.get累計ListItemsBy基準値(累計.list, e基準値);

    // 初行設定込みで初期化
    const templateRows: number[] = [templateConfigList.header];
    const headerLabels: (string | null)[] = [`【累計】\n${AbstractデータService.getMoneyUnitLabel()}`, null];
    const 累計header: 決算期TableHeader[] = [
      {
        label: `計画${基準値Key}\n${会計期間Label}`,
        isSummary: true,
        year: 0,
        決算年: 0,
      },
      ...累計.header,
    ];
    const values: (string | number | null)[][] = [[...headerLabels, ...累計header.map((h) => h.label)]];
    // 列の動的設定
    const dynamicConfigs: 帳票DynamicConfig[] = [
      {
        columns: [
          ..._.range(headerLabels.length),
          ...累計header.map((h, i) => (i === 累計header.length - 1 ? headerLabels.length + 1 : headerLabels.length)),
        ],
      },
    ];

    // 行でループ
    累計ListItems.forEach((l) => {
      // excelテンプレート行を設定
      const config = l.config;
      templateRows.push(this.getTemplateConfig(templateConfigList, config));
      const datas: (string | number | null)[] = this.createConfigLabels(config);

      if (l.value === undefined) {
        // valueなしの場合、すべてnull
        datas.push(...this.templateCellValues(4));
      } else {
        // valueありの場合、計画/実績/差異/比率 を追加
        datas.push(
          this.getPrintFormatValue(config, l.value.計画[基準値Key]),
          this.getPrintFormatValue(config, l.value.実績),
          l.value.業績予測.差異 === undefined ? '' : this.getMoneyUnitValue(l.value.業績予測.差異),
          l.value.業績予測.比率 === undefined ? '' : l.value.業績予測.比率 + '%'
        );
      }
      // 一行追加
      values.push(datas);
    });

    return {
      templateRows,
      values,
      dynamicConfigs,
    };
  }
}
